import {TpvDialog} from "../../components/TpvDialog";
import {recoverOrderToTpv} from "../../services/recoverOrder";
import {formatMoney} from "../../utils/pricing";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
// Redux
import { useDispatch, useSelector } from "react-redux";
// Utils/UI
import { formatCuteDate, translateMessage } from "@delibrandev/utils";
import { addIdKeyIfMissing } from "@delibrandev/utils/src/function.js";

import "./style.css";
import { OrderStatusLabel } from "../../components/orderStatus";
import { StateLabel } from "@delibrandev/utils";
import { OrderDetails } from "./orderDetails";
import { getOrders } from "../../api/orders";
import { getCustomerCarts } from "../../api/cart";
import { searchCustomerInformation } from "../../api/commander";
import { getCartTotalWithTax, getOrderTotalWithTax } from "../../utils/pricing";

import {
  setAlert,
  setComponentActive,
  setUserLogOut,
  updateCart,
  updateClient,
  updateOrder,
  updateRequestIsOrder,
  updateRequestIsRecoveredCart,
  initRequestPayments,
} from "../../reducers/userConfigSlice";

export const History = () => {
  const dispatch = useDispatch();
  const shippingMethods = useSelector(state => state.aplicationConfig.corporationData.pointMethods?.shipping) || [];
  const [search, setSearch] = useState("");
  const [reverseSort, setReverseSort] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [page, setPage] = useState(0);

  const [orderDetailsModal, setOrderDetailsModal] = useState(null);
  const [data, setData] = useState([]);
  const [activeSection, setActiveSection] = useState({
    type: "orders",
    orderStatus: null,
    paymentStatus: null,
  });

  const [recoverTarget,setRecoverTarget] = useState(null);
  const [recoverBusy,setRecoverBusy] = useState(false);
  const [recoverError,setRecoverError] = useState("");
  const recoverPending = useRef(false);
  const handleRecoverOrder = useCallback((order,options={})=>{setRecoverError("");setRecoverTarget({order,checkout:options.checkout===true});},[]);
  const confirmRecover = async () => {
    if(recoverPending.current || !recoverTarget)return;
    recoverPending.current=true;setRecoverBusy(true);setRecoverError("");
    try {await recoverOrderToTpv({orderId:recoverTarget.order.orderId,dispatch,checkout:recoverTarget.checkout,shippingMethods});setOrderDetailsModal(null);setRecoverTarget(null);}
    catch(error){setRecoverError(error.message);}
    finally{recoverPending.current=false;setRecoverBusy(false);}
  };

  const getQuery = useCallback(
    async (orderStatus = null, paymentStatus = null) => {
      try {
        const payload = {
          paymentStatus,
          orderStatus,
          pickupOnly: orderStatus === "prepared" ? true : undefined,
        };

        const response = await getOrders(payload);

        if (response && !response.error) {
          setData(Array.isArray(response?.data?.orders) ? response.data.orders : []);
        } else {
          dispatch(setAlert(translateMessage(response?.message || "Bad request")));
        }
      } catch (error) {
        console.error("[History] Error en getOrders:", error);

        dispatch(setUserLogOut());
      }
    },
    [dispatch],
  );

  const getAbandonedCarts = useCallback(async () => {
    try {
      const response = await getCustomerCarts(
        {
          customerId: "3409b4e8-17dc-461a-8284-905cbfbcb64d",
        },
        { page: 1, pageSize: 75 },
      );

      if (response && !response.error) {
        const carts = (Array.isArray(response?.data) ? response.data : []).filter((cart) => cart?.state !== "ACTIVE");

        setData(
          carts.map((cart) => ({
            ...cart,

            number: cart?.number ?? String(cart?.shoppingCartId ?? "").slice(0, 8),

            SalesChannel: null,

            Customer: cart?.Customer ?? cart?.customer ?? null,

            address: cart?.address ?? null,

            status: cart?.state ?? "ABANDONED",

            paymentStatus: null,

            orderId: null,

            isAbandonedCart: true,
          })),
        );
      } else {
        dispatch(setAlert(translateMessage(response?.message || "Bad request")));
      }
    } catch (error) {
      console.error("[History] Error en getCustomerCarts:", error);
    }
  }, [dispatch]);

  const handleRecoverCart = useCallback(
    async (cart) => {
      const customerFromCart = cart?.Customer ?? cart?.customer ?? {};
      const filter = customerFromCart?.phone || customerFromCart?.fullname;
      const shoppingCartId = cart?.shoppingCartId;

      if (!filter || !shoppingCartId) {
        dispatch(setAlert(translateMessage("No se ha podido identificar el cliente o el carrito")));
        return;
      }

      try {
        const customerResponse = await searchCustomerInformation(filter);
        const customers = Array.isArray(customerResponse?.data?.customer) ? customerResponse.data.customer : [];
        const normalizedFilter = String(filter).trim().toLowerCase();
        const customer =
          customers.find((item) => {
            const phone = String(item?.phone || "")
              .trim()
              .toLowerCase();
            const fullname = String(item?.fullname || "")
              .trim()
              .toLowerCase();
            return phone === normalizedFilter || fullname === normalizedFilter;
          }) ?? customers[0];

        if (!customer?.customerId) {
          dispatch(setAlert(translateMessage("No se ha encontrado el cliente del carrito")));
          return;
        }

        const cartsResponse = await getCustomerCarts({ customerId: customer.customerId }, { page: 1, pageSize: 75 });
        const recoveredCart = Array.isArray(cartsResponse?.data)
          ? cartsResponse.data.find((item) => String(item?.shoppingCartId) === String(shoppingCartId))
          : null;

        if (!recoveredCart) {
          dispatch(
            setAlert(translateMessage("El carrito no se encuentra entre los 75 carritos más recientes del cliente")),
          );
          return;
        }

        const currentAddresses =
          customer?.addresses && typeof customer.addresses === "object" ? customer.addresses : {};
        const cartAddress = recoveredCart?.address ?? cart?.address ?? null;
        const putAddressFirst = (list) => {
          if (!cartAddress) return Array.isArray(list) ? list : [];
          const addressId = cartAddress?.addressId;
          const previous = Array.isArray(list) ? list : [];
          return [
            cartAddress,
            ...previous.filter((address) => !addressId || String(address?.addressId) !== String(addressId)),
          ];
        };

        dispatch(
          updateClient({
            ...customer,
            addresses: {
              ...currentAddresses,
              shippingAddresses: putAddressFirst(currentAddresses.shippingAddresses),
              addresses: putAddressFirst(currentAddresses.addresses),
            },
          }),
        );
        dispatch(updateCart(recoveredCart));
        dispatch(updateOrder({ orderId: null }));
        dispatch(updateRequestIsOrder(false));
        dispatch(updateRequestIsRecoveredCart(true));
        dispatch(initRequestPayments());
        dispatch(setComponentActive("commander"));
      } catch (error) {
        console.error("[History] Error recuperando el carrito:", error);
        dispatch(setAlert(translateMessage("Error de red al recuperar el carrito")));
      }
    },
    [dispatch],
  );

  const refreshActiveSection = useCallback(async () => {
    if (activeSection.type === "carts") {
      await getAbandonedCarts();
      return;
    }

    await getQuery(activeSection.orderStatus, activeSection.paymentStatus);
  }, [activeSection, getAbandonedCarts, getQuery]);

  const selectOrderSection = useCallback(
    (orderStatus = null, paymentStatus = null) => {
      setActiveSection({
        type: "orders",
        orderStatus,
        paymentStatus,
      });

      getQuery(orderStatus, paymentStatus);
    },
    [getQuery],
  );

  const selectAbandonedCarts = useCallback(() => {
    setActiveSection({
      type: "carts",
      orderStatus: null,
      paymentStatus: null,
    });

    getAbandonedCarts();
  }, [getAbandonedCarts]);

  useEffect(() => {
    getQuery();
  }, [getQuery]);

  useEffect(() => {
    // Mientras el detalle está abierto no se refresca el historial.
    if (orderDetailsModal !== null) {
      return undefined;
    }

    let requestInProgress = false;

    const refreshInBackground = async () => {
      if (requestInProgress) return;

      requestInProgress = true;

      try {
        await refreshActiveSection();
      } finally {
        requestInProgress = false;
      }
    };

    const intervalId = window.setInterval(refreshInBackground, 10000);

    return () => window.clearInterval(intervalId);
  }, [refreshActiveSection, orderDetailsModal]);

  // =========== Datatable Configuration ============

  const sections = useMemo(
    () => [
      {
        text: "Todos",
        callback: () => selectOrderSection(),
        actions: ["search"],
        actionsWithSelect: [""],
      },
      {
        text: "Por cobrar",
        callback: () => selectOrderSection(null, "pending"),
        actions: ["search"],
        actionsWithSelect: [""],
      },
      {
        text: "Para recoger",
        callback: () => selectOrderSection("prepared", null),
        actions: ["search"],
        actionsWithSelect: [""],
      },
      {
        text: "En reparto",
        callback: () => selectOrderSection("inDelivery", null),
        actions: ["search"],
        actionsWithSelect: [""],
      },
      {
        text: "Entregado",
        callback: () => selectOrderSection("delivered", null),
        actions: ["search"],
        actionsWithSelect: [""],
      },
      {
        text: "Carritos abandonados",
        callback: selectAbandonedCarts,
        actions: ["search"],
        actionsWithSelect: [""],
      },
    ],
    [selectAbandonedCarts, selectOrderSection],
  );

  const customData = useMemo(
    () => ({
      number: (cell, row) => <>{row?.displayNumber ?? `${row?.SalesChannel?.identifier}-${row?.dailyNumber}`}</>,
      updatedAt: (cell) => formatCuteDate(cell),

      SalesChannel: (cell) => cell?.name ?? "",

      Customer: (cell) => cell?.fullname ?? "",

      total: (cell, row) => {
        const total = row?.isAbandonedCart ? getCartTotalWithTax(row) : getOrderTotalWithTax(row);
        return formatMoney(total);
      },

      address: (cell) => (cell?.street ? cell.street : ""),

      DeliveryUser: (cell) => cell?.fullname || "-",

      status: (cell) => (cell ? <OrderStatusLabel state={cell} /> : ""),

      paymentStatus: (cell) => (cell ? <StateLabel state={cell} /> : ""),

      orderId: (cell, row) =>
        row?.isAbandonedCart ? (
          <button type="button" className="tpvHistoryBring" onClick={() => handleRecoverCart(row)}>Recuperar carrito</button>
        ) : !row?.isAbandonedCart && row?.paymentStatus === "pending" ? (
          <button type="button" className="tpvHistoryBring" onClick={() => handleRecoverOrder(row)}>Traer al TPV</button>
        ) : (
          <></>
        ),
    }),
    [handleRecoverCart, handleRecoverOrder],
  );

  const tableData = useMemo(() => addIdKeyIfMissing(Array.isArray(data) ? data : []), [data]);

  const visibleRows = useMemo(
    () =>
      tableData
        .filter((row) =>
          `${row.displayNumber || ""} ${row.number || ""} ${row.dailyNumber || ""} ${row.Customer?.fullname || ""} ${row.Customer?.phone || ""}`
            .toLocaleLowerCase("es")
            .includes(search.toLocaleLowerCase("es")),
        )
        .sort((a, b) => (new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()) * (reverseSort ? -1 : 1)),
    [tableData, search, reverseSort],
  );
  // ================================================

  const handleOrder = useCallback((order) => {
    if (order?.isAbandonedCart) {
      return;
    }

    // Consultar el detalle no debe convertir ese pedido
    // en el pedido activo del TPV.
    setOrderDetailsModal(order);
  }, []);

  return (
    <main className="tpvHistory">
      <div className="tpvHistoryHeading">
        <div>
          <h1>Historial de pedidos</h1>
          <span>{data.length} registros</span>
        </div>
        <label className="tpvSearch">
          <span className="visually-hidden">Buscar pedidos</span>
          <input
            type="search"
            placeholder="Buscar pedido, cliente o teléfono…"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
          />
        </label>
      </div>
      <div className="tpvHistoryFilters">
        <div className="tpvHistoryTabs" aria-label="Filtrar historial">
          {sections.map((section, index) => (
            <button
              type="button"
              key={section.text}
              aria-pressed={selectedTab === index}
              className={selectedTab === index ? "is-active" : ""}
              onClick={() => {
                setSelectedTab(index);
                setPage(0);
                section.callback();
              }}
            >
              {section.text}
            </button>
          ))}
        </div>
        <button type="button" className="tpvSort" onClick={() => setReverseSort((value) => !value)}>
          {reverseSort ? "Más antiguos" : "Más recientes"}
        </button>
      </div>
      {selectedTab === 2 && <p className="tpvLegacyPickupHelp">Los pedidos antiguos sin tipo de entrega registrado se revisan desde Todos antes de confirmar su recogida.</p>}
      <div className="tpvHistoryRows">
        {visibleRows
          .slice(
            Math.min(page, Math.max(0, Math.ceil(visibleRows.length / 12) - 1)) * 12,
            (Math.min(page, Math.max(0, Math.ceil(visibleRows.length / 12) - 1)) + 1) * 12,
          )
          .map((row) => (
            <article className="tpvHistoryRow" key={row.orderId || row.shoppingCartId || row.id}>
              <button
                type="button"
                className="tpvHistoryIdentity"
                disabled={row.isAbandonedCart}
                onClick={() => handleOrder(row)}
              >
                <strong>#{customData.number(row.number, row)}</strong>
                <small>{customData.updatedAt(row.updatedAt)}</small>
              </button>
              <div className="tpvHistoryCustomer">
                <strong>{row.Customer?.fullname || "Cliente contado"}</strong>
                <small>{row.Customer?.phone || row.Customer?.email || ""}</small>
              </div>
              <div className="tpvHistoryChannel">
                <strong>{row.SalesChannel?.name || (row.isAbandonedCart ? "Carrito" : "TPV")}</strong>
                <small>{row.address?.street || row.ShippingMethod?.name || ""}</small>
              </div>
              <div className="tpvHistoryStatuses">
                {customData.paymentStatus(row.paymentStatus)}
                {customData.status(row.status)}
              </div>
              <strong className="tpvHistoryTotal">{customData.total(row.total, row)}</strong>
              <div className="tpvHistoryActions">
                {!row.isAbandonedCart && (
                  <button type="button" onClick={() => handleOrder(row)}>
                    Ver pedido →
                  </button>
                )}
                {customData.orderId(row.orderId, row)}
              </div>
            </article>
          ))}
        {!visibleRows.length && <div className="tpvNoResults">No hay resultados para esta selección.</div>}
      </div>

      <div className="tpvHistoryPagination">
        <span>{visibleRows.length} resultados</span>
        <button type="button" disabled={page <= 0} onClick={() => setPage((value) => Math.max(0, value - 1))}>
          Anterior
        </button>
        <button
          type="button"
          disabled={(page + 1) * 12 >= visibleRows.length}
          onClick={() => setPage((value) => value + 1)}
        >
          Siguiente
        </button>
      </div>
      {orderDetailsModal !== null && (
        <OrderDetails orderDetailsModal={orderDetailsModal} setOrderDetailsModal={setOrderDetailsModal} onOrderChanged={refreshActiveSection} onRecover={handleRecoverOrder} />
      )}
      {recoverTarget && <TpvDialog title={recoverTarget.checkout?"Cobrar pedido":"Traer pedido al TPV"} busy={recoverBusy} onClose={()=>setRecoverTarget(null)} className="tpvRecoverDialog"><div className="tpvDialogBody"><p>Se abrirá el pedido {recoverTarget.order.displayNumber || recoverTarget.order.number || "seleccionado"} en el TPV. La comanda actual se conserva y no se mezclan sus artículos.</p><p>Podrás modificar el pedido mientras Cocina no haya iniciado ninguna línea. Si ya está en preparación o preparado, podrás consultarlo. El reparto a domicilio lo cobra el repartidor.</p>{recoverError && <p role="alert">{recoverError}</p>}</div><footer><button type="button" className="tpvPrimary" disabled={recoverBusy} onClick={confirmRecover}>{recoverBusy?"Abriendo…":recoverTarget.checkout?"Abrir cobro":"Traer al TPV"}</button><button type="button" disabled={recoverBusy} onClick={()=>setRecoverTarget(null)}>Cancelar</button></footer></TpvDialog>}
    </main>
  );
};
